import { lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { useQuery } from "@tanstack/react-query";
import {
  CalendarIcon,
  ChartLineIcon,
  CompassIcon,
  InfoIcon,
  LightbulbIcon,
  MessageCircleIcon,
  PercentIcon,
} from "lucide-react";

import {
  getLocalStorageItem,
  localStorageIsSupported,
  setLocalStorageItem,
} from "simplydo/core/helpers";
import { initUseQuery } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";
import { useTheme } from "simplydo/react/theme";
import { Divider } from "simplydo/react/ui/divider";
import { Label } from "simplydo/react/ui/label";
import {
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import actions from "actions";
import api from "api";
import { ChallengeIdeaFiltersState } from "reducers/challengeIdeaFilters";
import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

import ChallengeIPStatementAgreement from "components/lib/ChallengeIPStatementAgreement";
import ChallengeStatusBar from "components/lib/ChallengeStatusBar";
import { ENTITY_TOOLBAR_HEIGHT } from "components/lib/EntityHeader";
import PageLoadError from "components/lib/PageLoadError";
import ReportContentChip from "components/lib/ReportContent/ReportContentChip";
import { SideNav } from "components/lib/SideNav";
import type { SideNavItemProps } from "components/lib/SideNav/SideNavComponent";
import { NAV_BAR_HEIGHT } from "components/lib/UI";

import { useChallengeQuery } from "../hooks/useChallengeQuery";
import ChallengeAssessments from "./Assessments";
import ChallengeHeader from "./Header";
import ChallengeIdeas from "./Ideas";
import NewIdea from "./NewIdea";
import ChallengeOverview from "./Overview";
import ChallengePlanner from "./Planner/PlannerMenu";

const ChallengeActivity = lazy(() => import("./Activity/ChallengeActivity"));
const ChallengeQuestions = lazy(() => import("./Questions/Questions"));

// Wrapper func component for when visiting the project ideas page
// Ensures that if you visit challenges/<challenge_id>/board, the view type is set to board
const ChallengeIdeasProjectWrapper = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const updateIdeaViewType = useCallback(
    (newIdeaViewType: string) => {
      dispatch(actions.challengeIdeaFilters.setIdeaViewType(newIdeaViewType));
    },
    [dispatch],
  );

  useEffect(() => {
    updateIdeaViewType("board");
  }, [updateIdeaViewType]);

  return (
    <Navigate
      to={`/challenges/${params.id}/ideas${location.search}`}
      replace={true}
    />
  );
};

const Challenge = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [_searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  // The header banner is only shown on the details (overview) page
  const isDetailsPage = location.pathname === `/challenges/${params.id}`;
  const challengeId = params.id?.replace("assessments", "");

  const ideaViewType = useAppSelector(
    (state) => state.challengeIdeaFilters.ideaViewType,
  );
  const urlOrganisation = useAppSelector(
    (state) => state.organisations?.urlOrganisation,
  );

  const { challenge, challengeQuery, user, canManageChallenge } =
    useChallengeQuery({ challengeId });

  const dispatch = useDispatch();
  const reloadUser = useUserReload();
  const addEngagement = useCallback(
    (engagement: string) => dispatch(actions.user.addEngagement(engagement)),
    [dispatch],
  );
  const updateFilters = useCallback(
    (data: Partial<ChallengeIdeaFiltersState>) =>
      dispatch(actions.challengeIdeaFilters.update(data)),
    [dispatch],
  );
  const onFollow = useCallback(
    (context: string, id: string) =>
      dispatch(actions.user.onFollow(context, id)),
    [dispatch],
  );
  const updateIdeaViewType = useCallback(
    (newIdeaViewType: string) => {
      dispatch(actions.challengeIdeaFilters.setIdeaViewType(newIdeaViewType));
    },
    [dispatch],
  );

  const [firstView, setFirstView] = useState(true);
  const [hasSignedStatement, setHasSignedStatement] = useState(false);
  const [hasClickedNewIdea, setHasClickedNewIdea] = useState(false);
  const [creatingNewIdea, setCreatingNewIdea] = useState(false);
  const [ipManagementInfo, setIpManagementInfo] = useState<
    Partial<OpenAPI.GET<"/challenges/{id}/ipstatements/statements">["response"]>
  >({
    userHasSigned: false,
    hasSensitiveInfo: false,
    ipStatementEnabled: false,
  });
  const [ipStatement, setIpStatement] = useState<
    OpenAPI.Schemas["IpStatement"] | null
  >(null);
  const [ipStatementAgreementOpen, setIpStatementAgreementOpen] =
    useState(false);
  const userId = user?._id;
  const orgIpManagementEnabled = util.organisationFeaturesEnabled(user, [
    "ipManagement",
  ]);
  const challengePlannerEnabled = util.organisationFeaturesEnabled(user, [
    "challengePlanner",
  ]);

  const myIdeasQuery = useQuery(
    initUseQuery(api.challenges.getIdeas, [challengeId!, { scope: "me" }], {
      isEnabled: !!userId && !!challengeId,
    }),
  );
  const myIdeas = myIdeasQuery.data?.ideas || [];

  const assignedAssessmentQuery = useQuery(
    initUseQuery(api.challenges.getAssignedAssessments, [challengeId!], {
      isEnabled: !!userId,
    }),
  );
  const myAssignedAssessments = useMemo(
    () => assignedAssessmentQuery.data?.ideas || [],
    [assignedAssessmentQuery.data],
  );

  const [isCollapsed, setIsCollapsed] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    if (firstView) {
      window.scrollTo(0, 0);
      updateFilters({
        page: 1,
        sort: challenge?.defaultIdeaOrder || "default",
        tags: [],
        authorGroupIds: [],
        fieldChoices: [],
        query: "",
      });
      setFirstView(false);
    }
  }, [firstView, challenge?.defaultIdeaOrder, updateFilters]);

  useEffect(() => {
    if (userId) {
      addEngagement("viewedChallenge");
    }
  }, [userId, addEngagement]);

  useEffect(() => {
    const { ipManagement = {} } = challenge || {};
    if (orgIpManagementEnabled && ipManagement?.isEnabled) {
      api.challenges.getLatestIpStatement(
        challengeId!,
        (info) => {
          if (info.ipStatementEnabled) {
            setIpManagementInfo(info);
            setHasSignedStatement(info.userHasSigned);
            setIpStatement(info.activeStatement);
          }
        },
        () => {},
      );
    }
  }, [challengeId, challenge, orgIpManagementEnabled]);

  const newIdea = () => {
    window.scrollTo(0, 0);
    setHasClickedNewIdea(true);
    if (
      ipStatement &&
      ipManagementInfo.ipStatementEnabled &&
      !hasSignedStatement
    ) {
      setIpStatementAgreementOpen(true);
      return;
    }
    setCreatingNewIdea(true);
    util.newIdea(challenge, user, (idea) => {
      navigate(`/ideas/${idea._id}`);
      if (user) {
        reloadUser();
        onFollow("challenge", idea.challenge);
      }
      setHasClickedNewIdea(false);
      setCreatingNewIdea(false);
    });
  };

  useEffect(() => {
    if (
      hasClickedNewIdea &&
      !creatingNewIdea &&
      ipStatement &&
      ipManagementInfo.ipStatementEnabled &&
      hasSignedStatement
    ) {
      setCreatingNewIdea(true);
      util.newIdea(challenge, user, (idea) => {
        navigate(`/ideas/${idea._id}`);
        if (user) {
          reloadUser();
          onFollow("challenge", idea.challenge);
        }
        setHasClickedNewIdea(false);
        setCreatingNewIdea(false);
      });
    }
  }, [
    hasSignedStatement,
    ipStatement,
    ipManagementInfo.ipStatementEnabled,
    hasClickedNewIdea,
    creatingNewIdea,
    challenge,
    user,
    navigate,
    onFollow,
    reloadUser,
  ]);

  const redirectToLogin = useCallback(
    (orgCode?: string) => {
      const currentOrg =
        challenge?.ownerOrganisation ||
        ({ code: orgCode } as OpenAPI.Schemas["Organisation"]);
      const searchParams = new SearchParams(location.search);
      const redirectPath = `/login?utm-challenge=${challengeId}${searchParams.toSafeString("&")}&then=/challenges/${challengeId}/ideas/new`;
      if (user && currentOrg) {
        window.location.href = util.getBaseUrl(currentOrg, redirectPath);
      } else {
        navigate(redirectPath);
      }
    },
    [challengeId, challenge, location.search, navigate, user],
  );

  const onClickProjectBoardLink = useCallback(() => {
    updateIdeaViewType("board");
    navigate(`/challenges/${challengeId}/ideas?queryView=board`);
  }, [challengeId, navigate, updateIdeaViewType]);

  const onClickIdeasLink = useCallback(() => {
    if (localStorageIsSupported()) {
      const ideaViewPreference =
        getLocalStorageItem("ideaViewTypePreference") || "card";
      updateIdeaViewType(
        ["card", "list"].includes(ideaViewPreference)
          ? ideaViewPreference
          : "card",
      );
    } else {
      updateIdeaViewType("card");
    }
    navigate(`/challenges/${params.id}/ideas`);
  }, [navigate, params.id, updateIdeaViewType]);

  const ipStatementHandled =
    !util.organisationFeaturesEnabled(user, ["ipManagement"]) ||
    !ipManagementInfo.ipStatementEnabled ||
    (ipManagementInfo.ipStatementEnabled && ipManagementInfo.userHasSigned);

  const unsubmittedAssessments = useMemo(
    () =>
      myAssignedAssessments?.flatMap((item) =>
        item?.assessments?.filter(
          (assessment) => assessment?.isSubmitted !== true,
        ),
      ),
    [myAssignedAssessments],
  );

  const canViewIdeasTab = useMemo(() => {
    return (
      challenge?.ideaVisibility === "users" ||
      canManageChallenge ||
      util.hasPermission(user, "challenge.viewIdeas", challengeId)
    );
  }, [challenge, canManageChallenge, user, challengeId]);

  const canViewProjectBoardTab = useMemo(() => {
    return (
      user &&
      (canManageChallenge ||
        challenge?.projectManagementVisibility === "users" ||
        util.hasPermission(user, "challenge.viewProjectBoard", challengeId))
    );
  }, [user, canManageChallenge, challengeId, challenge]);

  const isProjectBoardPage =
    !!canViewProjectBoardTab &&
    ((location.pathname === `/challenges/${params.id}/ideas` &&
      ideaViewType === "board") ||
      location.pathname === `/challenges/${params.id}/board`);

  const canViewActivityTab = useMemo(() => {
    return (
      user &&
      (canManageChallenge ||
        util.hasPermission(user, "challenge.viewActivity", challengeId))
    );
  }, [user, canManageChallenge, challengeId]);

  const canViewPlannerTab = useMemo(() => {
    const calendarId = challenge?.calendar?._id;
    return (
      user &&
      challengePlannerEnabled &&
      calendarId &&
      (canManageChallenge ||
        util.hasPermission(user, "calendar.view", calendarId, challengeId))
    );
  }, [
    user,
    canManageChallenge,
    challengeId,
    challengePlannerEnabled,
    challenge?.calendar?._id,
  ]);

  const canViewAssessmentsTab = useMemo(() => {
    return (
      user &&
      (myAssignedAssessments?.length > 0 ||
        ((canManageChallenge ||
          util.hasPermission(user, "challenge.viewAssessments", challengeId)) &&
          challenge?.ownerIdeaTemplate?.assessments?.rounds &&
          challenge.ownerIdeaTemplate.assessments.rounds.length > 0))
    );
  }, [user, canManageChallenge, challengeId, myAssignedAssessments, challenge]);

  useEffect(() => {
    if (ideaViewType !== "board") {
      return;
    }
    if (canViewProjectBoardTab) {
      return;
    }
    // If the user has a preferred idea view type of board, but is not allowed to view the project board on this challenge, we adjust it back to card
    updateIdeaViewType("card");
    setSearchParams((prev) => {
      prev.delete("queryView");
      return prev;
    });
  }, [
    ideaViewType,
    canViewProjectBoardTab,
    setSearchParams,
    updateIdeaViewType,
  ]);

  if (challengeQuery.isError) {
    const usingOrg = user ? user.ownerOrganisation : urlOrganisation;
    if (location.pathname === `/challenges/${challengeId}/ideas/new`) {
      return (
        <PageLoadError
          title={t("challenge.errorNewIdea")}
          message={`Please contact an administrator${usingOrg ? ` for ${usingOrg?.name} ` : " "}to request permission if you think this is incorrect.`}
        />
      );
    }

    return (
      <PageLoadError
        title={t(user ? "challenge.errorUser" : "challenge.errorNoUser")}
        message={
          challengeQuery?.error?.message || "Unable to load at this time"
        }
      />
    );
  }

  return (
    <div>
      {challenge && (
        <Helmet>
          <title>
            {challenge
              ? challenge.name
              : t("common:capitalise", { key: "generic.challenge" })}
          </title>
          <meta
            property="og:title"
            content={
              challenge
                ? challenge.name
                : t("common:capitalise", { key: "generic.challenge" })
            }
          />
          <meta property="og:image" content={challenge.bannerImageUrl} />
          <meta
            property="og:url"
            content={`${util.getBaseUrl(user?.ownerOrganisation)}/challenges/${challenge._id}`}
          />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@_simplydo"></meta>
          <meta
            name="twitter:url"
            content={`${util.getBaseUrl(user?.ownerOrganisation)}/challenges/${challenge._id}`}
          />
          <meta name="twitter:title" content={challenge.name} />
          <meta name="twitter:image" content={challenge.bannerImageUrl} />
        </Helmet>
      )}
      {util.organisationFeaturesEnabled(user, ["ipManagement"]) &&
      challenge &&
      ipStatement ? (
        <ChallengeIPStatementAgreement
          challenge={challenge}
          isOpen={ipStatementAgreementOpen}
          isPreview={false}
          createNewStatement={() => {}}
          setHasSignedStatement={setHasSignedStatement}
          onClose={() => setIpStatementAgreementOpen(false)}
          ipManagementInfo={ipManagementInfo}
          setIpManagementInfo={setIpManagementInfo}
          ipStatement={ipStatement}
          setIpStatement={setIpStatement}
        />
      ) : null}
      {user && (
        <ChallengeStatusBar
          challengeId={challengeId!}
          newIdea={newIdea}
          myIdeas={myIdeas}
          overviewPage={true}
          redirectToLogin={redirectToLogin}
        />
      )}
      <div>
        <SideNav
          collapsible
          isCollapsed={!!isCollapsed}
          onCollapse={() => setIsCollapsed(true)}
          onExpand={() => setIsCollapsed(false)}
          loading={challengeQuery.isLoading}
          top={NAV_BAR_HEIGHT + ENTITY_TOOLBAR_HEIGHT}
          fullWidth={isProjectBoardPage}
          navItems={
            challengeQuery.isLoading
              ? [
                  {
                    key: "loading",
                    type: "custom" as const,
                    content: (
                      <Placeholder>
                        <PlaceholderParagraph>
                          <PlaceholderLine style={{ height: 40 }} />
                          <PlaceholderLine style={{ height: 40 }} />
                        </PlaceholderParagraph>
                      </Placeholder>
                    ),
                  },
                ]
              : ([
                  {
                    key: "overview",
                    type: "link" as const,
                    to: "..",
                    content: t("challenge.details.title"),
                    iconLeft: InfoIcon,
                    active: location.pathname === `/challenges/${params.id}`,
                  },
                  canViewIdeasTab
                    ? {
                        key: "ideas",
                        type: "link" as const,
                        content: `${t("common:capitalise", { key: "generic.ideas" })}`,
                        iconLeft: LightbulbIcon,
                        active:
                          location.pathname ===
                            `/challenges/${params.id}/ideas` &&
                          ideaViewType !== "board",
                        onClick: onClickIdeasLink,
                        iconRight:
                          user &&
                          challenge?.ideaCount &&
                          challenge.ideaCount > 0 ? (
                            <Label size="small" circular>
                              {challenge.ideaCount}
                            </Label>
                          ) : null,
                      }
                    : null,

                  canViewActivityTab
                    ? {
                        key: "activity",
                        type: "link" as const,
                        to: "../activity",
                        content: "Activity",
                        iconLeft: ChartLineIcon,
                        active: location.pathname.startsWith(
                          `/challenges/${params.id}/activity`,
                        ),
                      }
                    : null,

                  canViewAssessmentsTab
                    ? {
                        key: "assessments",
                        type: "link" as const,
                        to: "../assessments",
                        content: !canManageChallenge
                          ? "Your assessments"
                          : "Assessment",
                        iconLeft: PercentIcon,
                        iconRight:
                          unsubmittedAssessments?.length > 0 ? (
                            <Label size="small" color="red" circular>
                              {unsubmittedAssessments?.length}
                            </Label>
                          ) : null,
                        active:
                          location.pathname.indexOf(
                            `/challenges/${params.id}/assessments`,
                          ) > -1,
                      }
                    : null,

                  challenge?.questionsEnabled
                    ? {
                        key: "questions",
                        type: "link" as const,
                        to: "../questions",
                        content: t("common:capitalise", {
                          key: "generic.questions",
                        }),
                        iconLeft: MessageCircleIcon,
                        active:
                          location.pathname.indexOf(
                            `/challenges/${params.id}/questions`,
                          ) > -1,
                        iconRight: (
                          <>
                            {challenge?.publicQuestions &&
                            challenge.publicQuestions > 0 ? (
                              <Label size="small" circular>
                                {challenge.publicQuestions}
                              </Label>
                            ) : null}
                            {challenge?.nonPublicQuestions &&
                            challenge.nonPublicQuestions > 0 ? (
                              <Label size="small" circular color="red">
                                {challenge.publicQuestions}
                              </Label>
                            ) : null}
                          </>
                        ),
                      }
                    : null,

                  canViewPlannerTab
                    ? {
                        key: "planner",
                        type: "link" as const,
                        to: "../planner",
                        content: "Planner",
                        iconLeft: CalendarIcon,
                        active:
                          location.pathname.indexOf(
                            `/challenges/${params.id}/planner`,
                          ) > -1,
                      }
                    : null,

                  canViewProjectBoardTab
                    ? {
                        key: "projectManagement",
                        type: "link" as const,
                        content: "Project Board",
                        iconLeft: CompassIcon,
                        onClick: onClickProjectBoardLink,
                        active:
                          location.pathname ===
                            `/challenges/${params.id}/ideas` &&
                          ideaViewType === "board",
                      }
                    : null,
                ].filter((item) => Boolean(item)) as SideNavItemProps[])
          }
          main={
            <>
              {isDetailsPage ? (
                <div>
                  <ChallengeHeader
                    loading={challengeQuery.isLoading}
                    challenge={challenge}
                    myIdeas={myIdeas}
                    newIdea={newIdea}
                    isPreview={false}
                    redirectToLogin={redirectToLogin}
                  />
                </div>
              ) : null}
              {!challengeQuery.isSuccess ? (
                <Placeholder>
                  <PlaceholderLine style={{ height: 40, minWidth: "100%" }} />
                  <PlaceholderLine style={{ height: 250, minWidth: "100%" }} />
                  {isDetailsPage && (
                    <>
                      <PlaceholderLine
                        style={{ height: 40, minWidth: "100%", marginTop: 20 }}
                      />
                      <PlaceholderLine
                        style={{ height: 150, minWidth: "100%" }}
                      />
                    </>
                  )}{" "}
                </Placeholder>
              ) : challenge ? (
                <Routes>
                  <Route
                    path="*"
                    element={
                      <ChallengeOverview
                        challenge={challenge}
                        newIdea={newIdea}
                        ipStatement={ipStatement}
                        ipManagementInfo={{
                          hasSensitiveInfo:
                            ipManagementInfo.hasSensitiveInfo ?? false,
                          userHasSigned:
                            ipManagementInfo.userHasSigned ?? false,
                        }}
                        ipStatementHandled={!!ipStatementHandled}
                        setIpStatementAgreementOpen={
                          setIpStatementAgreementOpen
                        }
                        redirectToLogin={redirectToLogin}
                      />
                    }
                  />
                  {canViewProjectBoardTab ? (
                    <Route
                      path="board"
                      element={<ChallengeIdeasProjectWrapper />}
                    />
                  ) : null}
                  <Route
                    path="ideas/new"
                    element={<NewIdea redirectToLogin={redirectToLogin} />}
                  />
                  {(ideaViewType === "board"
                    ? canViewProjectBoardTab
                    : canViewIdeasTab) && (
                    <Route
                      path="ideas"
                      element={
                        <ChallengeIdeas
                          challenge={challengeQuery.data}
                          newIdea={newIdea}
                        />
                      }
                    />
                  )}
                  {canViewPlannerTab && challenge && (
                    <Route path="planner">
                      <Route
                        index
                        path="*"
                        element={<ChallengePlanner challenge={challenge} />}
                      />
                    </Route>
                  )}
                  {canViewAssessmentsTab && (
                    <Route path="assessments">
                      <Route
                        index
                        path="*"
                        element={
                          <ChallengeAssessments
                            challenge={challengeQuery.data}
                          />
                        }
                      />
                    </Route>
                  )}
                  {canViewActivityTab && (
                    <Route
                      path="activity/*"
                      element={
                        <ChallengeActivity challenge={challengeQuery.data} />
                      }
                    />
                  )}
                  {challenge?.questionsEnabled && (
                    <Route
                      path="questions"
                      element={
                        <ChallengeQuestions challenge={challengeQuery.data} />
                      }
                    />
                  )}
                </Routes>
              ) : null}
            </>
          }
          afterContent={
            <>
              <Divider />
              <ReportContentChip
                forType="challenge"
                forId={challengeId!}
                isCollapsed={!!isCollapsed}
              />
            </>
          }
        />
      </div>
    </div>
  );
};

export default Challenge;
